import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { CheckCircle2, HelpCircle, Sparkles, ShieldCheck } from "lucide-react";
import { PACKAGE_CATEGORIES } from "data/packages";
import Countdown from "components/ui/countdown";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "components/ui/accordion";
import SimpleHeader from "components/SimpleHeader";
import { cn } from "lib/utils";

const formatPrice = (n: number) => `₺${n.toLocaleString("tr-TR")}`;

const FAQS = [
  {
    id: "faq-1",
    q: "Sınırsız depolama alanı ne anlama geliyor?",
    a: "Sınırsız depolama, düğün veya organizasyon boyunca dosya boyutu veya adet sınırı olmaksızın dilediğiniz kadar yüksek çözünürlüklü fotoğraf ve video yükleyebileceğiniz anlamına gelir."
  },
  {
    id: "faq-2",
    q: "Depolama süreleri (6 Ay, 9 Ay, 12 Ay) bittiğinde ne olur?",
    a: "Belirtilen süre boyunca tüm fotoğraflarınız ve videolarınız orijinal kalitesinde bulut sunucularımızda saklanır. Süre dolmadan önce size e-posta ve SMS ile bilgilendirme yapılır; albümü tek tıkla arşiv olarak indirebilirsiniz."
  },
  {
    id: "faq-3",
    q: "Fiziki masa kartları ve el kartları ne zaman ulaşır?",
    a: "Tasarımınızın hazırlanmasının ardından kartlarınız 250-300gr orijinal baskı kağıdına yüksek kaliteli baskı ile üretilir ve sipariş tarihinden itibaren 7 gün içerisinde kargo ile adresinize gönderilir. (Yakın tarihli siparişler için lütfen iletişim kanallarımızdan bizimle iletişime geçiniz!)"
  },
  {
    id: "faq-4",
    q: "14 gün şartsız iade garantisi nasıl çalışır?",
    a: "Müşteri memnuniyetini garanti altına alıyoruz. Eğer fiziksel kartlarınızın basım işlemi henüz matbaada başlamadıysa, satın alım tarihinden itibaren 14 gün boyunca hiçbir neden belirtmeden anında tam ücret iadesi alabilirsiniz."
  }
];

export default function PackagesPage() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<{ catIndex: number; pkgIndex: number } | null>({
    catIndex: 0,
    pkgIndex: 2, // Standart Düğün (En popüler olanı) varsayılan seçili başlar
  });
  const [activeCatIndex, setActiveCatIndex] = useState(0);

  // Meta Pixel
  useEffect(() => {
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', 'ViewContent', {
        content_name: 'Paketler Sayfası',
        content_category: 'Düğün ve Nişan Paketleri',
        currency: 'TRY',
      });
    }
  }, []);

  const handleProceed = () => {
    if (selected) {
      try {
        const selectedPkg = PACKAGE_CATEGORIES[selected.catIndex].packages[selected.pkgIndex];

        // Meta Pixel: InitiateCheckout
        if (typeof window !== 'undefined' && (window as any).fbq) {
          (window as any).fbq('track', 'InitiateCheckout', {
            content_name: selectedPkg.name,
            value: selectedPkg.price,
            currency: 'TRY',
            num_items: 1,
          });
        }

        localStorage.setItem(
          "anIkareSelectedPackage",
          JSON.stringify({
            id: selectedPkg.id,
            name: selectedPkg.name,
            price: selectedPkg.price,
            freeTableCardQty: selectedPkg.freeTableCardQty || 0,
            freeHandCardQty: selectedPkg.freeHandCardQty || 0,
            freeBoardQty: selectedPkg.freeBoardQty || 0,
          })
        );
      } catch (e) {
        console.error("Paket kaydedilirken hata:", e);
      }
      navigate("/baski");
    }
  };

  const activeCategory = PACKAGE_CATEGORIES[activeCatIndex];
  const selectedPackageData = selected
    ? PACKAGE_CATEGORIES[selected.catIndex].packages[selected.pkgIndex]
    : null;

  return (
    <div className="bg-slate-50 text-foreground min-h-dvh font-sans relative overflow-hidden selection:bg-[#E45078]/20 selection:text-[#E45078]">
      
      {/* Arka Plandaki Estetik Pembe/Rose Degrade Işık Çemberleri */}
      <div className="absolute top-[-10%] left-[-10%] w-[40vw] h-[40vw] rounded-full bg-rose-200/40 blur-[120px] pointer-events-none z-0"></div>
      <div className="absolute bottom-[20%] right-[-10%] w-[50vw] h-[50vw] rounded-full bg-[#FFF1F6] blur-[150px] pointer-events-none z-0"></div>

      <SimpleHeader />
      <div className="h-16 md:h-20"></div>

      {/* Üst Kahraman Başlık Alanı - Daha Az Dikey Boşluk */}
      <header className="relative z-10 py-8 md:py-10 text-center">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-2 mb-3">
            <div className="inline-flex items-center gap-1.5 bg-[#E45078]/10 border border-[#E45078]/25 text-[#E45078] px-3 py-0.5 rounded-full text-xs font-bold shadow-sm">
              <Sparkles className="w-3.5 h-3.5" /> Lansmana Özel İndirimler!
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-neutral-900 leading-tight">
            Anılarınızı <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#E45078] via-rose-500 to-[#F26C8C]">Ölümsüzleştirin</span>
          </h1>
          <p className="mt-2 text-sm sm:text-base text-neutral-600 max-w-2xl mx-auto leading-relaxed">
            Misafirlerinizin çektiği tüm fotoğraf ve videoları anında toplayın. Paket seçiminizi yapıp QR kodlu baskı tasarımlarınızı hemen onaylayın!
          </p>
        </div>
      </header>

      {/* Ana İçerik Alanı */}
      <main className="relative z-10 pb-40">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          
          {/* Kategori Seçici Tablar */}
          <div className="flex justify-center mb-6 relative z-20">
            <div className="inline-flex p-1 bg-white/80 backdrop-blur-md border border-neutral-200/80 rounded-2xl shadow-sm">
              {PACKAGE_CATEGORIES.map((cat, idx) => {
                const isActive = activeCatIndex === idx;
                return (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setActiveCatIndex(idx);
                      // Otomatik olarak o kategorideki ilk paketi seçelim
                      setSelected({ catIndex: idx, pkgIndex: 0 });
                    }}
                    className={cn(
                      "px-6 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all duration-300 whitespace-nowrap cursor-pointer",
                      isActive
                        ? "bg-[#E45078] text-white shadow-md shadow-rose-100 scale-100"
                        : "text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50/50"
                    )}
                  >
                    {idx === 0 ? "✨ " : "🏷️ "} {cat.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="text-center mb-8">
            <p className="text-xs sm:text-sm text-neutral-500 max-w-lg mx-auto leading-relaxed">
              {PACKAGE_CATEGORIES[activeCatIndex].description}
            </p>
          </div>

          {/* PAKETLER GRID ALANI - Doğrudan ve 4 Sütunlu Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 pt-2">
            {activeCategory.packages.map((pkg, pkgIndex) => {
              const isSelected =
                selected?.catIndex === activeCatIndex && selected?.pkgIndex === pkgIndex;
              
              const isStandart = pkg.id === 'dugun-standart' || pkg.id === 'ekonomik-3';
              
              let badgeText = "";
              let accentColor = "from-neutral-500 to-slate-700";
              let cardIcon = "📱";
              let slogan = "Sadece Dijital QR Havuz";

              if (pkg.id === 'dugun-ozel') {
                badgeText = "En Ekonomik";
                accentColor = "from-slate-400 to-slate-600";
                cardIcon = "📱";
                slogan = "Anında Dijital Katılım";
              } else if (pkg.id === 'dugun-baslangic') {
                badgeText = "Sade & Şık";
                accentColor = "from-rose-400 to-rose-500";
                cardIcon = "🌸";
                slogan = "Küçük Organizasyonlar";
              } else if (pkg.id === 'dugun-standart') {
                badgeText = "⭐️ EN POPÜLER & AVANTAJLI";
                accentColor = "from-[#E45078] to-rose-600";
                cardIcon = "⭐";
                slogan = "En Çok Tercih Edilen";
              } else if (pkg.id === 'dugun-pro') {
                badgeText = "👑 VIP HER ŞEY DAHİL";
                accentColor = "from-amber-500 to-orange-600";
                cardIcon = "👑";
                slogan = "Eksiksiz VIP Deneyim";
              } else if (pkg.id === 'ekonomik-1') {
                badgeText = "Sade Başlangıç";
                accentColor = "from-blue-400 to-indigo-500";
                cardIcon = "🌱";
                slogan = "25 Adet Tek Yüz Kart";
              } else if (pkg.id === 'ekonomik-2') {
                badgeText = "Bütçe Dostu";
                accentColor = "from-teal-400 to-emerald-500";
                cardIcon = "🌿";
                slogan = "50 Adet Tek Yüz Kart";
              } else if (pkg.id === 'ekonomik-3') {
                badgeText = "⭐️ EN POPÜLER";
                accentColor = "from-[#E45078] to-rose-600";
                cardIcon = "🍀";
                slogan = "100 Adet Tek Yüz Kart";
              } else if (pkg.id === 'ekonomik-4') {
                badgeText = "Maksimum Fırsat";
                accentColor = "from-amber-500 to-orange-600";
                cardIcon = "🌳";
                slogan = "150 Adet Tek Yüz Kart";
              }

              return (
                <div
                  key={pkgIndex}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelected({ catIndex: activeCatIndex, pkgIndex })}
                  onKeyDown={(e) =>
                    (e.key === "Enter" || e.key === " ") &&
                    setSelected({ catIndex: activeCatIndex, pkgIndex })
                  }
                  className={cn(
                    "relative rounded-2xl border transition-all duration-300 cursor-pointer overflow-hidden flex flex-col group hover:-translate-y-1 hover:shadow-xl",
                    isStandart
                      ? "bg-gradient-to-b from-white via-[#FFF5F8] to-white border-rose-300 shadow-md"
                      : "bg-white border-neutral-200 shadow-sm",
                    isSelected
                      ? "ring-4 ring-rose-500/25 border-[#E45078]"
                      : ""
                  )}
                >
                  
                  {/* Popülerlik / VIP Üst Rozeti */}
                  {badgeText && (
                    <div className={cn(
                      "absolute top-0 left-0 right-0 text-center py-1 text-[9px] font-black tracking-wider uppercase text-white shadow-sm z-10 bg-gradient-to-r",
                      accentColor
                    )}>
                      {badgeText}
                    </div>
                  )}

                  {/* Süper Kompakt Kart Gövdesi */}
                  <Card className="border-0 bg-transparent shadow-none p-4 pt-7 flex flex-col justify-between flex-grow min-h-[400px]">
                    <div>
                      {/* Üst Kısım İkon ve Seçim Durumu */}
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xl" role="img" aria-label="Icon">{cardIcon}</span>
                        {isSelected && (
                          <span className="inline-flex items-center gap-1 text-[9px] font-bold text-white bg-[#E45078] px-2 py-0.5 rounded-full shadow-sm animate-pulse">
                            <CheckCircle2 className="w-2.5 h-2.5" /> Seçildi
                          </span>
                        )}
                      </div>

                      <CardHeader className="p-0 pb-2">
                        <CardTitle className={cn(
                          "text-base sm:text-lg font-black tracking-tight",
                          isStandart ? "text-[#E45078]" : "text-neutral-855"
                        )}>
                          {pkg.name}
                        </CardTitle>
                        <p className="text-[10px] font-bold text-neutral-400 mt-0.5">{slogan}</p>
                        
                        {/* Fiyat Gösterim Kutusu */}
                        <div className="flex flex-col mt-2 min-h-[68px] justify-center bg-neutral-50 p-2.5 rounded-xl border border-neutral-100">
                          {pkg.originalPrice && pkg.originalPrice > pkg.price ? (
                            <div className="flex flex-col">
                              <div className="flex items-center gap-1.5">
                                <span className="text-neutral-400 line-through text-[11px] font-semibold decoration-red-500">
                                  {formatPrice(pkg.originalPrice)}
                                </span>
                                <span className="bg-red-100 text-red-600 text-[8px] font-black px-1.5 py-0.5 rounded-full">
                                  %{Math.round(((pkg.originalPrice - pkg.price) / pkg.originalPrice) * 100)} İNDİRİM
                                </span>
                              </div>
                              <div className="flex items-baseline gap-1 mt-0.5">
                                <span className={cn(
                                  "text-2xl sm:text-3xl font-black tracking-tight",
                                  isSelected || isStandart ? "text-[#E45078]" : "text-neutral-800"
                                )}>
                                  {formatPrice(pkg.price)}
                                </span>
                              </div>
                              <span className="text-[10px] text-emerald-600 font-bold mt-0.5">🚚 Ücretsiz Kargo</span>
                              {pkg.discountDeadline && (
                                <div className="mt-0.5 scale-[0.85] origin-left">
                                  <Countdown targetDate={pkg.discountDeadline} />
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="flex flex-col">
                              <div className="flex items-baseline gap-1">
                                <span className="text-2xl sm:text-3xl font-black text-neutral-850">
                                  {formatPrice(pkg.price)}
                                </span>
                              </div>
                              <span className="text-[10px] text-emerald-600 font-bold mt-0.5">🚚 Ücretsiz Kargo</span>
                            </div>
                          )}
                        </div>
                      </CardHeader>

                      {/* Özellikler Listesi - Son Derece Kompakt Spacing */}
                      <CardContent className="p-0 text-xs text-neutral-600 space-y-1.5 mt-2.5 text-left border-t border-neutral-100 pt-2.5">
                        {pkg.features.map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-1.5">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-[2.5px] group-hover:scale-105 transition-transform" />
                            <span 
                              className="leading-snug text-[11.5px] sm:text-[12px]"
                              dangerouslySetInnerHTML={{ __html: feature }} 
                            />
                          </div>
                        ))}
                      </CardContent>
                    </div>

                    {/* Buton Alanı */}
                    <div className="mt-4">
                      <Button
                        className={cn(
                          "w-full py-4 text-xs font-black rounded-xl transition-all duration-300 active:scale-95 cursor-pointer",
                          isSelected
                            ? "bg-[#E45078] hover:bg-rose-600 text-white shadow-sm shadow-rose-200"
                            : isStandart
                              ? "bg-white border border-[#E45078] text-[#E45078] hover:bg-rose-50/30"
                              : "bg-neutral-900 hover:bg-neutral-800 text-white"
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelected({ catIndex: activeCatIndex, pkgIndex });
                          if (isSelected) handleProceed();
                        }}
                      >
                        {isSelected ? "Devam Et ➔" : "Bu Paketi Seç"}
                      </Button>
                    </div>
                  </Card>
                </div>
              );
            })}
          </div>

          {/* GÜVENİLİRLİK BİLGİSİ (Shield Banner) */}
          <div className="mt-12 bg-white border border-rose-100 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row items-center gap-4 max-w-4xl mx-auto shadow-sm">
            <div className="size-11 bg-emerald-50 text-emerald-500 rounded-xl grid place-items-center shrink-0">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div className="text-center sm:text-left">
              <h4 className="text-sm sm:text-base font-bold text-neutral-800 flex items-center justify-center sm:justify-start gap-1">
                %100 Memnuniyet & 14 Gün Koşulsuz İade Garantisi
              </h4>
              <p className="text-xs text-neutral-500 mt-0.5 leading-relaxed">
                Satın aldığınız günden itibaren matbaa basım aşaması başlamadan önce 14 gün boyunca iptal hakkınız bulunur. Ödediğiniz tutar hiçbir kesinti yapılmadan anında kartınıza iade edilir.
              </p>
            </div>
          </div>

          {/* SIKÇA SORULAN SORULAR (FAQ) BÖLÜMÜ */}
          <section className="mt-16 max-w-3xl mx-auto">
            <div className="text-center mb-6">
              <HelpCircle className="w-6 h-6 text-[#E45078] mx-auto mb-2" />
              <h2 className="text-xl sm:text-2xl font-extrabold text-neutral-900">Sıkça Sorulan Sorular</h2>
              <p className="text-xs text-neutral-500 mt-0.5">Paketlerimiz ve süreç hakkında en çok merak edilenler</p>
            </div>
            
            <div className="bg-white rounded-2xl p-4 sm:p-6 border border-neutral-200/80 shadow-sm">
              <Accordion type="single" collapsible className="w-full space-y-2">
                {FAQS.map((faq) => (
                  <AccordionItem 
                    key={faq.id} 
                    value={faq.id} 
                    className="border border-neutral-100 rounded-xl px-3 sm:px-4 py-0.5 hover:bg-rose-50/10 transition-colors"
                  >
                    <AccordionTrigger className="text-xs sm:text-sm font-bold text-neutral-800 hover:no-underline hover:text-[#E45078] text-left py-2.5">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-xs text-neutral-500 leading-relaxed pt-1.5 pb-2.5">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>

        </div>
      </main>

      {/* Yapışkan Alıcı Alt Panel (Sticky Bottom Bar) */}
      {selectedPackageData && (
        <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-rose-100 bg-white/95 backdrop-blur-md shadow-2xl py-3.5 animate-in slide-in-from-bottom duration-300">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex flex-col sm:flex-row items-center gap-1.5 sm:gap-3 text-center sm:text-left">
              <span className="bg-rose-100 text-[#E45078] text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider">Seçilen Paket</span>
              <div className="text-xs sm:text-sm text-neutral-800">
                <strong>{selectedPackageData.name}</strong> —{" "}
                <span className="text-[#E45078] font-black text-sm sm:text-base">
                  {formatPrice(selectedPackageData.price)}
                </span>
                {selectedPackageData.originalPrice && (
                  <span className="text-neutral-400 line-through ml-2 text-[10px] font-semibold">
                    {formatPrice(selectedPackageData.originalPrice)}
                  </span>
                )}
                <span className="text-emerald-600 font-extrabold text-[10px] ml-2.5">🚚 Ücretsiz Kargo</span>
              </div>
            </div>
            <div className="flex items-center gap-2.5 w-full sm:w-auto">
              <Button
                className="bg-[#E45078] hover:bg-rose-600 text-white font-extrabold w-full sm:w-auto px-8 py-4.5 rounded-xl text-xs shadow-md shadow-rose-200 transition-all duration-300 hover:scale-[1.02] active:scale-95"
                onClick={handleProceed}
              >
                Paketi Seç ve Devam Et ➔
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}